import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit, Mail, Phone, MessageSquare, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function BusinessDashboard() {
  const { user } = useAuth();
  const [myBusinesses, setMyBusinesses] = useState([]);
  const [leads, setLeads] = useState({});
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    city_id: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    opening_hours: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [businessesRes, categoriesRes, citiesRes] = await Promise.all([
        axios.get(`${API}/businesses/owner/my-businesses`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/cities`),
      ]);
      setMyBusinesses(businessesRes.data);
      setCategories(categoriesRes.data);
      setCities(citiesRes.data);

      const leadsData = {};
      for (const business of businessesRes.data) {
        const leadsRes = await axios.get(`${API}/leads/business/${business.id}`);
        leadsData[business.id] = leadsRes.data;
      }
      setLeads(leadsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/businesses`, formData);
      toast.success('Business listing created! Waiting for admin approval.');
      setCreateDialogOpen(false);
      setFormData({
        name: '',
        category_id: '',
        city_id: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        description: '',
        opening_hours: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create business listing');
    }
  };

  const handleUpdateBusiness = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/businesses/${selectedBusiness.id}`, formData);
      toast.success('Business updated successfully!');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update business');
    }
  };

  const handleEditClick = (business) => {
    setSelectedBusiness(business);
    setFormData({
      name: business.name,
      category_id: business.category_id,
      city_id: business.city_id,
      address: business.address,
      phone: business.phone,
      email: business.email,
      website: business.website || '',
      description: business.description,
      opening_hours: business.opening_hours || '',
    });
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="container mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold" data-testid="dashboard-title">Business Dashboard</h1>
            <p className="text-slate-600 mt-1">Manage your business listings and leads</p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-business-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Business
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Business Listing</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateBusiness} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Business Name *</Label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="business-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Select value={formData.city_id} onValueChange={(value) => setFormData({ ...formData, city_id: value })}>
                      <SelectTrigger data-testid="city-select">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Address *</Label>
                  <Input
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="address-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    data-testid="website-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Opening Hours</Label>
                  <Input
                    placeholder="e.g., Mon-Fri: 9AM-6PM"
                    value={formData.opening_hours}
                    onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })}
                    data-testid="hours-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="description-input"
                  />
                </div>

                <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="submit-business-button">
                  Create Business Listing
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="businesses" className="space-y-6">
          <TabsList data-testid="dashboard-tabs">
            <TabsTrigger value="businesses" data-testid="businesses-tab">My Businesses</TabsTrigger>
            <TabsTrigger value="leads" data-testid="leads-tab">Leads</TabsTrigger>
          </TabsList>

          <TabsContent value="businesses" className="space-y-6">
            {myBusinesses.length === 0 ? (
              <Card className="p-12 text-center" data-testid="no-businesses">
                <p className="text-slate-600 mb-4">You haven't created any business listings yet.</p>
                <Button onClick={() => setCreateDialogOpen(true)} className="rounded-full bg-primary hover:bg-primary/90">
                  Create Your First Business
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {myBusinesses.map((business) => (
                  <Card key={business.id} className="p-6 shadow-sm" data-testid={`business-card-${business.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2">{business.name}</h3>
                        <span className="text-sm text-green-600 font-medium">✓ Active Listing</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClick(business)}
                        data-testid={`edit-business-${business.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>

                    <p className="text-slate-600 mb-4 line-clamp-2">{business.description}</p>

                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span>{business.rating || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" />
                        <span>{leads[business.id]?.length || 0} leads</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="leads">
            {Object.keys(leads).length === 0 || Object.values(leads).every(l => l.length === 0) ? (
              <Card className="p-12 text-center" data-testid="no-leads">
                <p className="text-slate-600">No leads yet.</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {myBusinesses.map((business) => {
                  const businessLeads = leads[business.id] || [];
                  if (businessLeads.length === 0) return null;

                  return (
                    <Card key={business.id} className="p-6 shadow-sm" data-testid={`leads-${business.id}`}>
                      <h3 className="text-xl font-semibold mb-4">{business.name}</h3>
                      <div className="space-y-4">
                        {businessLeads.map((lead) => (
                          <div key={lead.id} className="border rounded-lg p-4" data-testid={`lead-${lead.id}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="font-semibold">{lead.name}</p>
                                <p className="text-sm text-slate-500">
                                  {new Date(lead.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-600" />
                                <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                                  {lead.email}
                                </a>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-600" />
                                <a href={`tel:${lead.phone}`} className="text-primary hover:underline">
                                  {lead.phone}
                                </a>
                              </div>
                              <p className="text-slate-700 mt-2">{lead.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Business Listing</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateBusiness} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Select value={formData.city_id} onValueChange={(value) => setFormData({ ...formData, city_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Address *</Label>
              <Input
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Opening Hours</Label>
              <Input
                placeholder="e.g., Mon-Fri: 9AM-6PM"
                value={formData.opening_hours}
                onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                required
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90">
              Update Business Listing
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
